'use client';

import FormLabel from '@c/forms/controls/FormLabel';
import AddAddressModal from '@c/modals/AddAddressModal';
import BaseModal from '@c/modals/BaseModal';
import Button from '@ui/Button';
import Checkbox from '@ui/Checkbox';
import Chip from '@ui/Chip';
import { useOrder } from '@util/hooks/useOrder';
import { useProduct } from '@util/hooks/useProduct';
import { useProducts } from '@util/hooks/useProducts';
import { formatCurrency } from '@util/index';
import { useReturns } from 'app/support/hooks/useReturns';
import { useAuth } from 'context/AuthContext';
import { AddressDocument } from 'models/address';
import { ProductDocument } from 'models/product';
import {
  EXTERNAL_LABEL_OPTIONS,
  ExternalLabelProvider,
  ReturnDocument,
  ReturnState,
  returnQty,
} from 'models/returns';
import { useMemo, useState } from 'react';

export const WAREHOUSE_ADDRESS: AddressDocument = {
  name: 'MX LOCKER',
  address_line1: '38415 INNOVATION CT STE E',
  address_line2: '',
  city_locality: 'MURRIETA',
  state_province: 'CA',
  postal_code: '92563-2600',
  country_code: 'US',
  phone: '+14359013371',
  is_default: false,
};

export const getAddressString = (a: AddressDocument) =>
  `${a.name} ${a.address_line1} ${a.address_line2 || ''} ${a.city_locality} ${
    a.state_province
  } ${a.postal_code}`;

export type ApproveReturnArgs = {
  id: string;
  ship_to: AddressDocument;
  charge: 'seller' | 'buyer' | null;
  og_label_charge: 'seller' | null;
  /**
   * Set when the label is coming from somewhere other than MX Locker, which
   * approves the return without buying one.
   */
  external_label?: ExternalLabelProvider | null;
  /** Other returns from this order and seller travelling in the same box. */
  group_ids?: string[];
  accept_higher_rate?: boolean;
  bypass_international_check?: boolean;
  override_weight_dimensions?: boolean;
  weight?: number;
  length?: number;
  width?: number;
  height?: number;
  carrier?: ReturnCarrier;
};

interface ApproveReturnModalProps {
  id: string;
  isOpen: boolean;
  dismiss: () => void;
  onApprove: (data: ApproveReturnArgs) => void;
  loading?: boolean;
  returnShippingEstimate: number;
  addresses: AddressDocument[];
  isInternational: boolean;
  isAdmin?: boolean;
  orderId: string;
  productId: string;
  sellerId: string;
  /**
   * Siblings that ship with this return when the whole group is being approved
   * at once. Their presence switches the modal from "approve this item, maybe
   * batch others with it" to "approve this return, here is what is in the box".
   */
  lockedGroupIds?: string[];
  fallbackAddress?: AddressDocument | null;
}

/**
 * A starting measurement for the box, taken from the listings the items
 * shipped out in. They stack inside one carton: weight adds up, the carton is
 * as long and wide as its biggest item and as tall as the pile.
 *
 * Null when no listing carries a package, leaving the fields empty rather than
 * suggesting a number nothing backs up. A package saved without its
 * measurements counts as zero, which the form reads the same way — as a field
 * still waiting to be filled in.
 */
function suggestPackage(
  returns: ReturnDocument[],
  products: ProductDocument[] | undefined
) {
  const packed = returns
    .map((ret) => ({
      qty: returnQty(ret),
      pkg: products?.find((p) => p.id === ret.product_id)?.shipment?.package,
    }))
    .filter((entry) => !!entry.pkg);

  if (!packed.length) return null;

  return {
    weight: Math.ceil(
      packed.reduce(
        (sum, { qty, pkg }) => sum + (pkg?.weight?.value ?? 0) * qty,
        0
      )
    ),
    length: Math.max(...packed.map(({ pkg }) => pkg?.dimensions?.length ?? 0)),
    width: Math.max(...packed.map(({ pkg }) => pkg?.dimensions?.width ?? 0)),
    height: packed.reduce(
      (sum, { qty, pkg }) => sum + (pkg?.dimensions?.height ?? 0) * qty,
      0
    ),
  };
}

/**
 * USPS measures a box as its longest side plus the girth of the other two and
 * refuses anything past 130 inches. UPS and FedEx allow 165, so a box over this
 * still ships — just never by mail.
 */
const USPS_MAX_LENGTH_PLUS_GIRTH = 130;

/**
 * Carriers a return can be pinned to. Blank books whichever quoted cheapest,
 * which is what nearly every return wants — pinning is for the box one carrier
 * refuses and another takes.
 */
const RETURN_CARRIERS = [
  { value: 'usps', label: 'USPS' },
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
] as const;

type ReturnCarrier = (typeof RETURN_CARRIERS)[number]['value'];

const lengthPlusGirth = (sides: number[]) => {
  const [longest, ...rest] = [...sides].sort((a, b) => b - a);
  return longest + 2 * rest.reduce((sum, side) => sum + side, 0);
};

const fieldStyles =
  'w-full rounded-[0.8rem] border border-brand-lighter-gray px-[1.5rem] py-[1.2rem] text-[1.6rem] text-brand-black outline-none transition-colors focus:border-brand-secondary';

/** A titled block of related controls, with an optional badge beside the title. */
function Section({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-[1.5rem]">
      <div className="flex flex-col gap-[0.5rem]">
        <div className="flex items-center justify-between gap-[1.5rem]">
          <h3 className="text-[2rem] font-bold tracking-[-0.01em] text-brand-black">
            {title}
          </h3>
          {action}
        </div>
        {!!description && (
          <p className="text-[1.5rem] font-medium leading-[1.5] text-text-muted">
            {description}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

const SectionDivider = () => (
  <span className="h-px w-full shrink-0 bg-brand-lighter-gray" />
);

const OUNCES_PER_POUND = 16;

/**
 * A number box with its unit pinned inside the right edge.
 *
 * It carries the text that was typed rather than a number, so an emptied box
 * stays empty. Handing it a number instead would turn a cleared box into a 0
 * that sits in front of whatever gets typed next.
 */
function UnitInput({
  unit,
  placeholder,
  value,
  onChange,
  onBlur,
}: {
  unit: string;
  // Left off where a real reading could be zero: a placeholder that reads as a
  // number is indistinguishable from that number once it is actually entered.
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
}) {
  return (
    <div className="relative w-full">
      <input
        type="number"
        min={0}
        placeholder={placeholder}
        className={`${fieldStyles} pr-[4.5rem]`}
        value={value}
        onBlur={onBlur}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="pointer-events-none absolute right-[1.5rem] top-1/2 -translate-y-1/2 text-[1.4rem] font-medium text-text-muted">
        {unit}
      </span>
    </div>
  );
}

/** One measurement of the carton, named above the box and united inside it. */
function DimensionField({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <FormLabel value={label} labelClasses="text-[1.5rem]">
      <UnitInput
        unit="in"
        placeholder={placeholder}
        value={Number.isFinite(value) && value ? String(value) : ''}
        onChange={(raw) => onChange(Number(raw) || 0)}
      />
    </FormLabel>
  );
}

/**
 * Weight goes in the way a scale reads it and comes out the way the label wants
 * it: pounds and ounces in, total ounces out.
 *
 * The two boxes are one number, so ounces past 15 roll into the pounds beside
 * them — 1 lb 20 oz settles as 2 lb 4 oz. That happens when a box is left
 * rather than on each keystroke: carrying mid-word would rewrite "20" into "4"
 * between the two digits being typed.
 */
function WeightField({
  ounces,
  onChange,
}: {
  ounces: number;
  onChange: (ounces: number) => void;
}) {
  // A total that is not a real number would render both boxes empty and turn
  // every keystroke back into itself, leaving the field impossible to type in.
  const total = Number.isFinite(ounces) ? ounces : 0;
  // Once there is a weight both boxes carry a number, so a zero reads as a
  // weighed zero rather than a box left blank. Before that both sit empty.
  const weighed = total > 0;
  // The text in each box while it is being typed in, held apart from the
  // settled total until the box is left. Undefined once nothing is being
  // edited, so the boxes go back to reading the total.
  const [typed, setTyped] = useState<{ lb: string; oz: string }>();

  const lbValue =
    typed?.lb ?? (weighed ? String(Math.floor(total / OUNCES_PER_POUND)) : '');
  const ozValue =
    typed?.oz ?? (weighed ? String(total % OUNCES_PER_POUND) : '');

  const edit = (next: { lb: string; oz: string }) => {
    const nextTotal =
      (Number(next.lb) || 0) * OUNCES_PER_POUND + (Number(next.oz) || 0);
    setTyped(next);
    onChange(nextTotal);
  };

  const settle = () => {
    setTyped(undefined);
  };

  return (
    <FormLabel value="Weight" labelClasses="text-[1.5rem]">
      <div className="grid grid-cols-2 gap-[1rem]">
        <UnitInput
          unit="lb"
          value={lbValue}
          onChange={(lb) => edit({ lb, oz: ozValue })}
          onBlur={settle}
        />
        <UnitInput
          unit="oz"
          value={ozValue}
          onChange={(oz) => edit({ lb: lbValue, oz })}
          onBlur={settle}
        />
      </div>
    </FormLabel>
  );
}

/**
 * "Klim Rift Jacket ×2 (+$12.50 back to the refund)" — the product title lives
 * on the product document, not on the return, so each row fetches its own.
 */
function useBoxItemLabel(ret: ReturnDocument) {
  const { data: productQuery } = useProduct(ret.product_id);
  const product = productQuery?.data;
  const shippingBack = ret.return_shipping_estimate
    ? ` (+$${ret.return_shipping_estimate.toFixed(2)} back to the refund)`
    : '';

  return `${product?.title ?? ret.product_id} ×${returnQty(
    ret
  )}${shippingBack}`;
}

/** A return that can be added to or left out of this box. */
function GroupCandidate({
  ret,
  selected,
  onChange,
}: {
  ret: ReturnDocument;
  selected: boolean;
  onChange: (selected: boolean) => void;
}) {
  const label = useBoxItemLabel(ret);

  return (
    <Checkbox
      label={label}
      selected={selected}
      onChange={(value) => onChange(!!value)}
    />
  );
}

/**
 * A return listed without a choice attached: either the whole group is being
 * approved, or this is the return that owns the label and so cannot leave its
 * own box. `indented` lines the title up with adjacent checkbox rows.
 */
function BoxItemRow({
  ret,
  tag,
  indented,
}: {
  ret: ReturnDocument;
  tag?: string;
  indented?: boolean;
}) {
  const label = useBoxItemLabel(ret);

  return (
    <div className="flex items-center justify-between gap-[1rem]">
      <span
        className={`text-[1.4rem] font-medium text-brand-black ${
          indented ? 'pl-[3.5rem] sm:pl-[2.6rem]' : ''
        }`}
      >
        {label}
      </span>
      {!!tag && (
        <span className="shrink-0 whitespace-nowrap rounded-[0.6rem] bg-brand-lightest-gray px-[0.8rem] py-[0.4rem] text-[1.2rem] font-semibold text-text-muted">
          {tag}
        </span>
      )}
    </div>
  );
}

function ApproveReturnModalForm({
  id,
  dismiss,
  onApprove,
  loading,
  returnShippingEstimate,
  addresses,
  isInternational,
  isAdmin,
  orderId,
  productId,
  sellerId,
  lockedGroupIds,
  fallbackAddress,
}: Omit<ApproveReturnModalProps, 'isOpen'>) {
  const [returnToAddress, setReturnToAddress] =
    useState<AddressDocument | null>(null);
  // empty means MX Locker buys the label, which is how nearly every return goes
  const [externalLabel, setExternalLabel] = useState<
    ExternalLabelProvider | ''
  >('');
  const [charge, setCharge] = useState<'seller' | 'buyer' | null>(null);
  const [chargeOriginalLabel, setChargeOriginalLabel] = useState(false);
  const [bypassInternationalCheck, setBypassInternationalCheck] =
    useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [extraAddresses, setExtraAddresses] = useState<AddressDocument[]>([]);
  const [overrideWeightDimensions, setOverrideWeightDimensions] =
    useState(false);
  const [shipTogether, setShipTogether] = useState(false);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  // null means untouched, so the measurements taken from the products can fill
  // the field until the operator overrides them
  const [carrier, setCarrier] = useState<ReturnCarrier | ''>('');
  const [weight, setWeight] = useState<number | null>(null);
  const [length, setLength] = useState<number | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const allAddresses = useMemo(
    () => [...addresses, ...extraAddresses],
    [addresses, extraAddresses]
  );
  const { userDoc } = useAuth();
  const {
    data: { data: order },
  } = useOrder(orderId);
  const { returnsByOrder, convertPartialRefundMutation } = useReturns({
    orderId,
    uid: userDoc?.uid,
  });
  const item = order?.sellers
    ? Object.values(order.sellers)
        .flat()
        .find((s) => s.id === productId)
    : null;
  const isMXLabel = !!item?.rate_id;

  // Only pending returns from the same seller can share this label — a return
  // already approved has its own, and another seller's items ship elsewhere.
  const pendingBoxReturns = (returnsByOrder ?? []).filter(
    (ret) =>
      ret.seller_id === sellerId && ret.state === ReturnState.ReturnRequested
  );
  const labelOwnerReturn = pendingBoxReturns.find((ret) => ret.id === id);
  // The buyer keeps the items on a partial refund, so a label bought here
  // covers a package nobody is sending. The API refuses these outright; this
  // says so before the operator fills the form in.
  const partialRefundRequest =
    labelOwnerReturn?.resolution === 'partial' ? labelOwnerReturn : null;
  const groupCandidates = pendingBoxReturns.filter((ret) => ret.id !== id);
  // Siblings that already left with a label. Approving alongside them buys a
  // second one for a box that is already paid for and already has a tracking
  // number, so the operator has to see them.
  const alreadyLabelled = (returnsByOrder ?? []).filter(
    (ret) =>
      ret.id !== id && ret.seller_id === sellerId && !!ret.tracking_number
  );
  // Approving the whole group settles every pending return at once; approving a
  // single item only pulls in siblings the operator ticks.
  const approvesWholeGroup = !!lockedGroupIds?.length;
  // A sibling can be approved from somewhere else between the card rendering
  // and this modal opening, so only ids still pending go onto the label.
  const candidateIds = new Set(groupCandidates.map((ret) => ret.id));
  const chosenGroupIds = approvesWholeGroup
    ? lockedGroupIds
    : shipTogether
    ? groupIds
    : [];
  const selectedGroupIds = chosenGroupIds.filter((groupId) =>
    candidateIds.has(groupId)
  );
  const isGrouped = selectedGroupIds.length > 0;
  // Approving a group whose members we cannot account for would settle one
  // return and silently leave the rest pending. An empty array counts as
  // loaded, so checking the list arrived is not enough — every locked sibling
  // has to actually be in it.
  const unaccountedGroupIds = approvesWholeGroup
    ? (lockedGroupIds ?? []).filter((groupId) => !candidateIds.has(groupId))
    : [];
  // Whether a label is allowed at all is read off the return document, so
  // approving before the list arrives can buy one for a partial refund, which
  // ships nothing. Both paths wait; only the group path also needs its siblings
  // accounted for.
  const awaitingReturns = !returnsByOrder;
  const awaitingCandidates =
    awaitingReturns ||
    (approvesWholeGroup &&
      (unaccountedGroupIds.length > 0 || !labelOwnerReturn));
  // Everything travelling in this box, label owner first.
  const boxReturns = [
    ...(labelOwnerReturn ? [labelOwnerReturn] : []),
    ...groupCandidates.filter((ret) => selectedGroupIds.includes(ret.id)),
  ];
  const {
    data: { data: boxProducts },
  } = useProducts(boxReturns.map((ret) => ret.product_id));
  const suggestedPackage = suggestPackage(boxReturns, boxProducts);

  // A label from the seller or bought by the buyer leaves nothing here to pay
  // for, pick a carrier for, or measure a box for.
  const buysLabel = !externalLabel;
  const wholeGroupDescription = buysLabel
    ? `All ${boxReturns.length} items are being approved together and ship in one box on a single label. Return shipping is charged once.`
    : `All ${boxReturns.length} items are being approved together and come back in one box. No label is bought for them here.`;
  const shipTogetherDescription = buysLabel
    ? 'Everything in this box travels on one label and tracking number. Return shipping is charged once, on the return you are approving.'
    : 'Everything in this box travels back together under one tracking number. No label is bought for them here.';
  const alreadyLabelledDescription = `${
    alreadyLabelled.length === 1
      ? 'Another return'
      : `${alreadyLabelled.length} other returns`
  } from this order already shipped with a label and tracking number. ${
    buysLabel
      ? 'Approving this one issues a second label, so the buyer gets two for the same box.'
      : 'This one is being approved without a label of ours, so check the buyer is not sending the same box twice.'
  }`;
  const packageWeight = weight ?? suggestedPackage?.weight ?? 0;
  const packageLength = length ?? suggestedPackage?.length ?? 0;
  const packageWidth = width ?? suggestedPackage?.width ?? 0;
  const packageHeight = height ?? suggestedPackage?.height ?? 0;
  // A box holding several products weighs nothing like any one of them, so
  // grouping forces the operator to measure it.
  const sendsPackage = buysLabel && (isGrouped || overrideWeightDimensions);
  const packageIsIncomplete =
    sendsPackage &&
    (!packageWeight || !packageLength || !packageWidth || !packageHeight);
  const packageGirth = lengthPlusGirth([
    packageLength,
    packageWidth,
    packageHeight,
  ]);
  const exceedsUspsGirth =
    !!packageLength &&
    !!packageWidth &&
    !!packageHeight &&
    packageGirth > USPS_MAX_LENGTH_PLUS_GIRTH;

  const toggleGroupId = (groupId: string, selected: boolean) =>
    setGroupIds((prev) =>
      selected
        ? [...prev, groupId]
        : prev.filter((prevId) => prevId !== groupId)
    );

  // A request that reached us without its amount would otherwise read as a
  // request for $0.00, which is a number nobody asked for. Say where to find it
  // instead.
  const partialRefundAmount = partialRefundRequest?.partial_refund_amount;
  const partialRefundWarning =
    typeof partialRefundAmount === 'number'
      ? `The buyer is keeping this item and asked for ${formatCurrency(
          partialRefundAmount
        )} back. Nothing is shipping, so there is no label to buy — refund that amount instead, or turn this into a standard return and have the items sent back. Their reason is the first note on this return.`
      : 'The buyer is keeping this item and asked for a partial refund; the sum they want is in the first note on this return, along with their reason. Nothing is shipping, so there is no label to buy — refund it instead, or turn this into a standard return and have the items sent back.';

  // Puts the items back on the road so the request becomes approvable. The
  // buyer's ask is dropped along the way, so the note is the record of it.
  const handleConvertToStandardReturn = () => {
    if (!partialRefundRequest) return;
    convertPartialRefundMutation.mutate({ returnDoc: partialRefundRequest });
  };

  const handleApprove = () => {
    const address = returnToAddress ?? fallbackAddress;
    if (!address) {
      return;
    }
    onApprove({
      id,
      ship_to: address,
      charge: buysLabel ? charge : null,
      bypass_international_check: bypassInternationalCheck,
      og_label_charge: buysLabel && chargeOriginalLabel ? 'seller' : null,
      ...(externalLabel ? { external_label: externalLabel } : {}),
      ...(buysLabel && carrier ? { carrier } : {}),
      ...(isGrouped ? { group_ids: selectedGroupIds } : {}),
      ...(sendsPackage
        ? {
            override_weight_dimensions: true,
            weight: packageWeight,
            length: packageLength,
            width: packageWidth,
            height: packageHeight,
          }
        : {}),
    });
  };

  return (
    <>
      <div className="flex w-full flex-col sm:w-[70rem]">
        <div className="flex max-h-[65vh] flex-col gap-[2rem] overflow-y-auto p-[2.5rem]">
          {!!partialRefundRequest && (
            <div className="flex flex-col items-start gap-[1.2rem] rounded-[1rem] border-l-[0.4rem] border-brand-secondary bg-brand-light-red px-[1.5rem] py-[1.2rem]">
              <p className="text-[1.6rem] font-bold leading-[1.4] text-text-red">
                {partialRefundWarning}
              </p>
              <Button
                type="tertiary"
                text="Turn into a standard return"
                height="small"
                loading={convertPartialRefundMutation.isPending}
                onClick={handleConvertToStandardReturn}
              />
            </div>
          )}

          <Section
            title="Return Label"
            description="Who is producing the label the item comes back on."
          >
            <select
              className={fieldStyles}
              value={externalLabel}
              onChange={(e) =>
                setExternalLabel(e.target.value as ExternalLabelProvider | '')
              }
            >
              <option value="">MX Locker buys the label</option>
              {EXTERNAL_LABEL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {!buysLabel && (
              <p className="rounded-[0.8rem] bg-brand-lightest-gray px-[1.5rem] py-[1.2rem] text-[1.5rem] font-medium leading-[1.5] text-brand-black">
                No label is bought, so nothing is charged for one and the buyer
                is told in the return chat where to ship and to send the
                tracking number back. Add it under Update Tracking Number once
                they do — the return sits in Awaiting Dropoff until then.
                {/* The refund was worked out when the return was opened, so a
                    deduction for shipping we are no longer paying for stays on
                    it until someone takes it off by hand. */}
                {returnShippingEstimate > 0 &&
                  ` $${returnShippingEstimate.toFixed(
                    2
                  )} of return shipping still comes out of this refund; adjust the amount when refunding.`}
              </p>
            )}
          </Section>

          <SectionDivider />

          <Section
            title="Return Address"
            description="Where the buyer ships the item back to."
            action={
              <Chip
                text={`MX Label: ${isMXLabel ? 'Yes' : 'No'}`}
                color={isMXLabel ? 'success' : 'error'}
                fontWeight="medium"
                className="shrink-0 !py-[0.4rem] !text-[1.4rem]"
              />
            }
          >
            <select
              className={fieldStyles}
              onChange={(e) => {
                if (e.target.value === 'other') {
                  setShowAddressModal(true);
                  return;
                }
                const address = allAddresses.find(
                  (a) => getAddressString(a) === e.target.value
                );
                if (!address) return;
                setReturnToAddress(address);
              }}
              value={
                showAddressModal
                  ? 'other'
                  : returnToAddress
                  ? getAddressString(returnToAddress)
                  : ''
              }
            >
              <option value="">Please select an address</option>
              {allAddresses.map((a) => (
                <option key={getAddressString(a)} value={getAddressString(a)}>
                  {getAddressString(a)}
                </option>
              ))}
              <option value="other">Other...</option>
            </select>
            {!!isAdmin && isInternational && (
              <Checkbox
                label="Bypass international check"
                selected={bypassInternationalCheck}
                onChange={(selected) => {
                  setBypassInternationalCheck(selected ?? false);
                }}
              />
            )}
          </Section>

          {buysLabel && (
            <>
              <SectionDivider />

              <Section title="Charges">
                <div className="flex flex-col gap-[1.2rem]">
                  <div className="flex flex-col gap-[0.6rem]">
                    <Checkbox
                      label="Charge buyer for return label (out of refund)"
                      selected={charge === 'buyer'}
                      onChange={(selected) => {
                        setCharge(selected ? 'buyer' : null);
                      }}
                    />
                    {/* The estimate is already deducted from the refund, so
                        ticking the box on top of it charges the buyer for the
                        label twice. */}
                    {returnShippingEstimate > 0 && (
                      <p className="text-[1.4rem] font-medium leading-[1.4] text-text-muted">
                        ${returnShippingEstimate.toFixed(2)} of return shipping
                        already comes out of this refund. Tick this only to
                        charge the label on top of that.
                      </p>
                    )}
                  </div>
                  <Checkbox
                    label="Charge seller for return label (direct charge)"
                    selected={charge === 'seller'}
                    onChange={(selected) => {
                      setCharge(selected ? 'seller' : null);
                    }}
                  />
                  {isMXLabel && (
                    <Checkbox
                      label="Charge seller for original label (direct charge)"
                      selected={chargeOriginalLabel}
                      onChange={(selected) => {
                        setChargeOriginalLabel(selected ?? false);
                      }}
                    />
                  )}
                </div>
              </Section>
            </>
          )}

          {approvesWholeGroup ? (
            <>
              <SectionDivider />

              <Section
                title="Items In This Return"
                description={wholeGroupDescription}
              >
                <div className="flex flex-col gap-[1.2rem] rounded-[1rem] border border-brand-lighter-gray bg-brand-off-white p-[1.5rem]">
                  {boxReturns.map((ret) => (
                    <BoxItemRow key={ret.id} ret={ret} />
                  ))}
                </div>
                {awaitingCandidates && (
                  <p className="text-[1.5rem] font-medium leading-[1.5] text-brand-secondary">
                    {returnsByOrder
                      ? 'Some returns in this group are no longer awaiting review, so they cannot be approved together. Close this and reopen it to see the current contents of the box.'
                      : 'Loading the rest of this return…'}
                  </p>
                )}
              </Section>
            </>
          ) : (
            groupCandidates.length > 0 && (
              <>
                <SectionDivider />

                <Section
                  title="Ship Together"
                  description={shipTogetherDescription}
                >
                  <Checkbox
                    label="Ship other returns from this order in the same box"
                    selected={shipTogether}
                    onChange={(selected) => {
                      setShipTogether(selected ?? false);
                      if (!selected) setGroupIds([]);
                    }}
                  />
                  {shipTogether && (
                    <div className="flex flex-col gap-[1.2rem] rounded-[1rem] border border-brand-lighter-gray bg-brand-off-white p-[1.5rem]">
                      {!!labelOwnerReturn && (
                        <BoxItemRow
                          ret={labelOwnerReturn}
                          tag="Approving now"
                          indented
                        />
                      )}
                      {groupCandidates.map((ret) => (
                        <GroupCandidate
                          key={ret.id}
                          ret={ret}
                          selected={groupIds.includes(ret.id)}
                          onChange={(selected) =>
                            toggleGroupId(ret.id, selected)
                          }
                        />
                      ))}
                    </div>
                  )}
                </Section>
              </>
            )
          )}

          {alreadyLabelled.length > 0 && (
            <>
              <SectionDivider />

              <Section
                title="Already On A Label"
                description={alreadyLabelledDescription}
              >
                <div className="flex flex-col gap-[1.2rem] rounded-[1rem] border border-brand-lighter-gray bg-brand-off-white p-[1.5rem]">
                  {alreadyLabelled.map((ret) => (
                    <BoxItemRow key={ret.id} ret={ret} tag="Has a label" />
                  ))}
                </div>
              </Section>
            </>
          )}

          {buysLabel && (
            <>
              <SectionDivider />

              <Section
                title="Package"
                description={
                  isGrouped
                    ? 'Grouped returns ship in one box — enter the weight and dimensions of the whole package.'
                    : undefined
                }
              >
                {/* Grouping already makes the measurements mandatory, so the
                    toggle would be stuck on with no way to turn it off. */}
                {!isGrouped && (
                  <Checkbox
                    label="Override weight/dimensions"
                    selected={overrideWeightDimensions}
                    onChange={(selected) => {
                      setOverrideWeightDimensions(selected ?? false);
                    }}
                  />
                )}
                <FormLabel value="Carrier" labelClasses="text-[1.5rem]">
                  <select
                    className={fieldStyles}
                    value={carrier}
                    onChange={(e) =>
                      setCarrier(e.target.value as ReturnCarrier | '')
                    }
                  >
                    <option value="">Cheapest available</option>
                    {RETURN_CARRIERS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FormLabel>
                {exceedsUspsGirth && (
                  <p className="rounded-[0.8rem] bg-brand-lightest-gray px-[1.5rem] py-[1.2rem] text-[1.5rem] font-medium leading-[1.5] text-brand-black">
                    This box measures {Number(packageGirth.toFixed(2))}
                    &quot; in length plus girth, past the{' '}
                    {USPS_MAX_LENGTH_PLUS_GIRTH}&quot; USPS accepts. It ships
                    UPS or FedEx instead
                    {sendsPackage
                      ? ', or bring it under by editing the measurements below.'
                      : ', or override the measurements to bring it under.'}
                  </p>
                )}
                {/* Weight and the carton it is measured in are separate readings
                    taken off separate tools, so they never share a row. */}
                {sendsPackage && (
                  <div className="flex flex-col gap-[2rem]">
                    <WeightField ounces={packageWeight} onChange={setWeight} />

                    <div className="flex flex-col">
                      <span className="mb-[0.8rem] block text-[1.5rem] font-medium text-black">
                        Package
                      </span>
                      <div className="grid grid-cols-3 gap-[1rem]">
                        <DimensionField
                          label="Length"
                          placeholder="18"
                          value={packageLength}
                          onChange={setLength}
                        />
                        <DimensionField
                          label="Width"
                          placeholder="15"
                          value={packageWidth}
                          onChange={setWidth}
                        />
                        <DimensionField
                          label="Height"
                          placeholder="10"
                          value={packageHeight}
                          onChange={setHeight}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </Section>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-[1.6rem] border-t border-brand-lighter-gray px-[2.5rem] py-[2rem]">
          {awaitingReturns && (
            <p className="mr-auto text-[1.5rem] font-medium leading-[1.5] text-brand-secondary">
              Loading this return…
            </p>
          )}
          <Button
            type="tertiary"
            text="Cancel"
            height="small"
            onClick={dismiss}
          />
          <Button
            type="secondary"
            text={buysLabel ? 'Approve Return' : 'Approve Without Label'}
            height="small"
            loading={loading}
            disabled={
              !returnToAddress ||
              packageIsIncomplete ||
              awaitingCandidates ||
              !!partialRefundRequest
            }
            onClick={handleApprove}
          />
        </div>
      </div>
      {showAddressModal && (
        <AddAddressModal
          isOpen={showAddressModal}
          skipValidation
          dontUpdateUserDoc
          dismiss={(address) => {
            setShowAddressModal(false);
            if (address) {
              setExtraAddresses((prev) => [...prev, address]);
              setReturnToAddress(address);
            }
          }}
        />
      )}
    </>
  );
}

function ApproveReturnModal({
  id,
  isOpen,
  dismiss,
  onApprove,
  loading,
  returnShippingEstimate,
  addresses,
  isInternational,
  isAdmin,
  fallbackAddress,
  orderId,
  productId,
  sellerId,
  lockedGroupIds,
}: ApproveReturnModalProps) {
  return (
    <BaseModal
      title="Approve Return"
      isOpen={isOpen}
      overflowType="visible"
      dismiss={dismiss}
    >
      {isOpen ? (
        <ApproveReturnModalForm
          id={id}
          dismiss={dismiss}
          onApprove={onApprove}
          loading={loading}
          returnShippingEstimate={returnShippingEstimate}
          addresses={addresses}
          isInternational={isInternational}
          isAdmin={isAdmin}
          fallbackAddress={fallbackAddress}
          orderId={orderId}
          productId={productId}
          sellerId={sellerId}
          lockedGroupIds={lockedGroupIds}
        />
      ) : null}
    </BaseModal>
  );
}

export default ApproveReturnModal;
